/**
 * Composant de route protégée
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@features/auth';
import { ReactNode } from 'react';
import { getAccueilParRole, ADMIN_SLUGS, PAIE_SLUGS } from '@shared/utils/getAccueilParRole';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'admin' | 'user' | 'paie' | 'employe';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, isHydrated } = useAuth();
  const location = useLocation();

  // Afficher un loader pendant l'hydratation Zustand uniquement
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Rediriger vers login si non authentifié (prioritaire sur isLoading)
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Afficher un loader pour les autres cas de chargement (login en cours, etc.)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const role = user?.role;
  const accueil = getAccueilParRole(role);

  // Vérifier le rôle si requis — rediriger vers la page naturelle du rôle réel
  if (requiredRole === 'admin') {
    if (!role || !ADMIN_SLUGS.includes(role)) {
      return <Navigate to={accueil} replace />;
    }
  }

  if (requiredRole === 'paie') {
    if (!role || !PAIE_SLUGS.includes(role)) {
      return <Navigate to={accueil} replace />;
    }
  }

  if (requiredRole === 'employe') {
    if (!role || role !== 'employe') {
      return <Navigate to={accueil} replace />;
    }
  }

  return <>{children}</>;
}

export default ProtectedRoute;
